import { telemetry } from '../../../@vendor/Posthog';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { ObjectManager } from '../../../concepts/objects';
import { ServerDate } from '../../../platform/ServerDate';
import { alert, sendAlert } from '../../packages/alerts/AlertCommands';
import { ProfileModel } from '../../ProfileModel';
import { BoardInfo } from '../BoardInfo';
import { EditorSession } from '../EditorSession';
import { MyProfileInfo } from '../MyProfileInfo';
import { Meeting } from './Meeting';
import { resolveActiveMeeting, resolveActiveMeetingAndPhase } from './MeetingUtil';
import { Phase } from './Phase';
import { SetupMeetingModel } from './SetupMeetingModel';

export const meetingStart = defineCommand(
    'meetingStart',
    'Meeting: Start',
    async (r, silent?: boolean) => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);
        const me = session.get(MyProfileInfo);

        const spec = SetupMeetingModel.createDefaultFreestyle(r.call(ProfileModel));
        if (spec && spec.facilitators.length <= 0) {
            dispatch(alert, ['You cannot start a meeting without at least one facilitator!']);
            return false;
        }

        await dispatch(meetingStop, [!spec]);
        if (!spec) return true;

        objects.update(u => {
            u.set('history', 'skip');

            const meeting = u.create(Meeting);
            for (const ps of spec.phases) {
                const phase = u.create(Phase);
                phase.key = ps.key;
                phase.name = ps.name;
                phase.time = ps.time * 60 * 1000;
                phase.tools = Object.entries(ps.tools).filter(x => x[1]).map(x => x[0]);
                phase.incognito = ps.incognito;
                phase.concealment = ps.concealment;
                meeting.add(phase);
            }
            meeting.boardId = session.get(BoardInfo).id;
            meeting.mode = spec.mode;
            meeting.facilitators = Array.from(spec.facilitators);
            meeting.welcome = spec.welcome;
            meeting.startLocked = spec.startLocked;

            // If start locked, set active phase to -1 so no phase is active
            if (meeting.startLocked) {
                meeting.activePhase = -1;
            }
            else {
                meeting.activePhase = 0;
                meeting.timeBank = spec.phases[0].time * 60 * 1000;
                !(silent === true) && dispatch(sendAlert, [`${me.name} just started a new meeting!`]);
            }
        });

        telemetry.emit('interaction.meeting', {
            boardId: session.get(BoardInfo).id
        });

        return true;
    },
    []
);

export const meetingStop = defineCommand(
    'meetingStop',
    'Meeting: Stop',
    (r) => {

        const objects = r.call(ObjectManager);

        // Remove meeting and phases objects
        objects.update(u => {
            u.set('history', 'skip');

            const phases = objects.getAll(Phase);
            for (const p of phases) {
                p.removeSelf();
                u.delete(p);
            }
            const meeting = objects.getAll(Meeting);
            for (const m of meeting) {
                u.delete(m);
            }
        });

    },
    []
);

export const meetingBreak = defineCommand(
    'meetingBreak',
    'Meeting: Break',
    (r, [length]: [number?]) => {
        length = length || 15 * 60 * 60 * 1000;

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const meeting = resolveActiveMeeting(objects);
        if (!meeting) return;

        objects.update(u => {
            u.set('history', 'skip');
            meeting.breakLength = length;
            meeting.breakStart = ServerDate.now();
            dispatch(meetingPauseTimer);
        });

    },
    []
);

export const meetingResume = defineCommand(
    'meetingResume',
    'Meeting: Resume',
    r => {

        const objects = r.call(ObjectManager);
        const meeting = resolveActiveMeeting(objects);
        if (!meeting) return;

        objects.update(u => {
            u.set('history', 'skip');
            meeting.breakStart = 0;
        });

    },
    []
);

export const meetingToggleTimer = defineCommand(
    'meetingToggleTimer',
    'Meeting: Toggle Timer',
    (r, [enabled]: [string?]) => {


        const objects = r.call(ObjectManager);
        const meeting = resolveActiveMeeting(objects);
        if (!meeting) return false;

        objects.update(u => {
            u.set('history', 'skip');

            meeting.timer = (!!enabled && enabled === 'true' ? true : !meeting.timer);
        });
    },
);

export const meetingStartTimer = defineCommand(
    'meetingStartTimer',
    'Meeting: Start Timer',
    r => {

        const objects = r.call(ObjectManager);
        const meeting = resolveActiveMeeting(objects);
        if (!meeting) return false;

        objects.update(u => {
            u.set('history', 'skip');

            if (meeting.timePaused) meeting.timePaused = 0;
            meeting.timeStarted = ServerDate.now();
        });
    },
);

export const meetingPauseTimer = defineCommand(
    'meetingPauseTimer',
    'Meeting: Pause Timer',
    r => {

        const objects = r.call(ObjectManager);
        const meeting = resolveActiveMeeting(objects);
        if (!meeting) return false;

        objects.update(u => {
            u.set('history', 'skip');

            if (meeting.timeStarted && !meeting.timePaused) {
                const now = ServerDate.now();
                meeting.timePaused = now;
                meeting.timeBank = Math.max(0, meeting.timeBank - (now - meeting.timeStarted));
                meeting.timeStarted = 0;
            }

        });
    },
);

export const meetingSetTimer = defineCommand(
    'meetingSetTime',
    'Meeting: Set Timer...',
    (r, [timeMs]: [number?]) => {

        const objects = r.call(ObjectManager);
        const meeting = resolveActiveMeeting(objects);
        if (!meeting) return;

        objects.update(u => {
            u.set('history', 'skip');
            meeting.timeStarted = 0;
            meeting.timePaused = 0;
            meeting.timeBank = timeMs;
        });
    },
);

export const meetingIncrementTimer = defineCommand(
    'meetingIncrementTimer',
    'Meeting: Increment Timer...',
    (r, [timeMs]: [number?]) => {

        const objects = r.call(ObjectManager);
        const meeting = resolveActiveMeeting(objects);
        if (!meeting) return;

        objects.update(u => {
            u.set('history', 'skip');

            const reading = meeting.getTimeReadingMs();

            if (reading <= 0) {
                meeting.timeBank = timeMs;
                if (meeting.timeStarted) meeting.timeStarted = ServerDate.now();
                if (meeting.timePaused) meeting.timePaused = ServerDate.now();
            }
            else {
                if (reading < timeMs) {
                    meeting.timeBank += timeMs - reading;
                }
                else {
                    meeting.timeBank += timeMs;
                }
            }

        });
    },
);

export const meetingResetTimer = defineCommand(
    'meetingResetTimer',
    'Meeting: Reset Timer',
    (r) => {

        const objects = r.call(ObjectManager);
        const [meeting, phase] = resolveActiveMeetingAndPhase(objects);
        if (!meeting || !phase) return;

        objects.update(u => {
            u.set('history', 'skip');
            meeting.timePaused = 0;
            meeting.timeStarted = 0;
            meeting.timeBank = 0;
        });
    }
);

export const meetingNext = defineCommand(
    'meetingNext',
    'Meeting: Goto Next Phase',
    r => {

        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const meeting = resolveActiveMeeting(objects);
        if (!meeting) return;

        const nextPhase = meeting.children[meeting.activePhase + 1]?.as(Phase);
        if (!nextPhase) return;

        objects.update(u => {
            u.set('history', 'skip');
            const now = ServerDate.now();
            meeting.timePaused = 0;
            meeting.timeStarted = now;
            meeting.timeBank = nextPhase.time;
            meeting.activePhase += 1;
        });

        const msg = `The ${nextPhase.name} activity has started!`;
        dispatch(sendAlert, [msg]);

    },
    []
);

export const meetingPrev = defineCommand(
    'meetingPrev',
    'Meeting: Goto Previous Phase',
    r => {

        const objects = r.call(ObjectManager);
        const [meeting, phase] = resolveActiveMeetingAndPhase(objects);
        if (!meeting || !phase) return;

        const prevPhase = meeting.children[meeting.activePhase - 1]?.as(Phase);

        objects.update(u => {
            u.set('history', 'skip');

            const now = ServerDate.now();
            meeting.timePaused = 0;
            meeting.timeStarted = !!prevPhase ? now : 0;
            meeting.timeBank = !!prevPhase ? prevPhase.time : 0;
            meeting.activePhase -= 1;
        });

    },
    []
);

export const meetingSetHosts = defineCommand(
    'meetingSetHosts',
    'Meeting: Set Host(s)',
    (r, userIds: string[]) => {

        const objects = r.call(ObjectManager);
        const meeting = resolveActiveMeeting(objects);
        if (!meeting) return;

        objects.update(u => {
            u.set('history', 'skip');
            meeting.facilitators = userIds ?? [];
        });

    },
    []
);

export const meetingSetTools = defineCommand(
    'meetingSetTools',
    'Meeting: Set Tools',
    (r, toolIds: string[]) => {

        const objects = r.call(ObjectManager);
        const [meeting, phase] = resolveActiveMeetingAndPhase(objects);
        if (!meeting) return;

        objects.update(u => {
            u.set('history', 'skip');
            phase.tools = toolIds ?? [];
        });
    },
    []
);

export const inMeetingCommands = () => inCommands(
    meetingStart,
    meetingStop,
    meetingBreak,
    meetingResume,
    meetingToggleTimer,
    meetingSetTimer,
    meetingStartTimer,
    meetingPauseTimer,
    meetingResetTimer,
    meetingNext,
    meetingPrev,
);
